import * as Yup from "yup";
import { FormikProvider, useFormik } from "formik";
import { PaymentInfo, NegotiationReactivationType, TYPE_INSTALLMENTS } from "../../../../../../../types/payment.type";
import { IRenter } from "../../../../../../../types/renter.type";
import { useReactivationNegotiation } from "./customHook/useReactivationNegotiatino";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import InfoGrid from "../../../../../../../common/components/ui/form/InfoGrid";
import InputFormik from "../../../../../../../common/components/form/InputFormik";
import TextAreaFormik from "../../../../../../../common/components/form/TextAreaFormik";
import InputMoney from "../../../../../../../common/components/form/CurrencyInputFormik";
import { InstallmentOption, useCalculateInstallmentOptions } from "../../../../../helpers/calculationUtils";
import { useEffect, useMemo, useState } from "react";
import ComboBoxFormik from "../../../../../../../common/components/form/ComboBoxFormik";
import SlideToggle from "../../../../../../../common/components/ui/toggleSwitch/SlideToggle";


type FormReactivationNegotiationProps = {
  onSubmit: (values: NegotiationReactivationType) => Promise<{ ok: boolean, data: NegotiationReactivationType, errors?: unknown[] }>;
  renter: IRenter
  payments: PaymentInfo[]
}

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Descrição é obrigatória'),
  downPayment: Yup.string().required('Valor da entrada é obrigatório'),
  installmentNegotiatedValue: Yup.string().required('Valor da negociação a ser parcelado é obrigatório'),
  installmentOption: Yup.string().required('Escolha a opção de parcelamento'),
  maintenanceFineAmount: Yup.number().required('Valor da multa por manutenção é obrigatório').min(0, 'Valor deve ser maior ou igual a 0'),
  revisionFineAmount: Yup.string()
    .required('Valor da multa por revisão é obrigatória')
    .test('is-valid-number', 'Valor deve ser maior ou igual a 0', (value) => {
      if (!value) return false;

      // Remove possíveis vírgulas e converte para número
      const numericValue = parseFloat(value.replace(',', '.'));
      const result =numericValue >= 0;
      return result
    }),
  paymentOptions: Yup.string().required('Escolha a opção de pagamento'),
  nextDueDate: Yup.string()
    .required("Data de cobrança é obrigatória")
    .test(
      'future-date',
      "A data não pode ser anterior à data atual",
      (value) => {
        if (!value) return false;

        // Cria a data atual sem horas (00:00:00)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Converte a data do input para objeto Date
        const parts = value.split('/');
        const inputDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        inputDate.setHours(0, 0, 0, 0);

        // Comparação segura usando timestamp
        return inputDate.getTime() >= today.getTime();
      }
    ),
});

export function FormReactivationNegotiation(props: FormReactivationNegotiationProps) {
  const renter = props.renter;
  const [hasRetrievalAndBlocking, setFasRetrievalAndBlocking] = useState(false);
  const { unlinkDate, licensePlate, percentualValueSplit, overdueWeeksValue, vehicleOwnerId, paymentId, gridItems, paymentOptions } = useReactivationNegotiation({ renter, payments: props.payments });


  const initialValues = {
    description: "",
    downPayment: "",
    hasRetrievalAndBlocking: false,
    installmentNegotiatedValue: 0,
    installmentOption: "",
    licensePlate: licensePlate,
    maintenanceFineAmount: 0,
    negotiationAmount: 0,
    nextDueDate: "",
    overdueWeeksValue: overdueWeeksValue,
    partnerId: vehicleOwnerId,
    paymentId: paymentId,
    paymentOptions: "",
    percentualValueSplit: percentualValueSplit,
    reference: TYPE_INSTALLMENTS.REACTIVATION,
    renterId: renter._id!,
    revisionFineAmount: 0,
    unlinkDate: unlinkDate,
    valueInstallment: "",
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: NegotiationReactivationType) => {
      const result = await props.onSubmit(values);
      if (result.ok) {
        formik.resetForm();
      }
    }
  })


  const calculateInstallmentOptions = useCalculateInstallmentOptions();
  const installmentOptions: InstallmentOption[] = useMemo(() => {
    return calculateInstallmentOptions(formik.values.installmentNegotiatedValue, 20)
  }, [formik.values.installmentNegotiatedValue, calculateInstallmentOptions]);

  useEffect(() => {
    const valueSelectedOption = installmentOptions.find(
      option => option.value === formik.values.installmentOption
    );
    const installmentAmount = valueSelectedOption?.valueOption || 0;
    if (installmentAmount && Number(formik.values.valueInstallment) !== Number(installmentAmount)) {
      formik.setFieldValue('valueInstallment', installmentAmount);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.installmentOption, installmentOptions]);

  useEffect(() => {
    const PaymentValue = overdueWeeksValue ? overdueWeeksValue : 0;
    const revisionFineAmount = formik.values.revisionFineAmount ? formik.values.revisionFineAmount : 0;
    const maintenanceFineAmount = formik.values.maintenanceFineAmount ? formik.values.maintenanceFineAmount : 0;
    const valueRetrievalAndBlocking = hasRetrievalAndBlocking ? 220 : 0;

    const negotiationAmount = PaymentValue + revisionFineAmount + maintenanceFineAmount + valueRetrievalAndBlocking;
    formik.setFieldValue('negotiationAmount', negotiationAmount);
    formik.setFieldValue('hasRetrievalAndBlocking', hasRetrievalAndBlocking);

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueWeeksValue, formik.values.revisionFineAmount, formik.values.maintenanceFineAmount, hasRetrievalAndBlocking]);

  useEffect(() => {
    const valueDownPayment = formik.values.installmentNegotiatedValue ? formik.values.negotiationAmount - formik.values.installmentNegotiatedValue : 0;

    formik.setFieldValue('downPayment', valueDownPayment.toFixed(2));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.installmentNegotiatedValue, formik.values.negotiationAmount])

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <div className="grid grid-cols-1">
          <InfoGrid items={gridItems} />

          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            <InputMoney
              label="Valor da multa por revisão"
              name="revisionFineAmount"
              required={true}
            />
            <InputMoney
              label="Valor da multa por manutenção"
              name="maintenanceFineAmount"
              required={true}
            />
            <div className="flex  justify-center items-center">
              <label className="font-semibold text-[14px]">Multa por recolhimento e Bloqueio?</label>
              <SlideToggle
                name="hasRetrievalAndBlocking"
                className="ml-2"
                onToggle={(checked) => setFasRetrievalAndBlocking(checked)}
              />
              <span className="ml-2">{hasRetrievalAndBlocking ? "Sim" : "Não"}</span>
            </div>
          </section>
          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
            <InputMoney
              label="valor da negociação"
              value={formik.values.negotiationAmount}
              name="negotiationAmount"
              required={true}
            />
            <InputMoney
              label="Valor da negociação a ser parcelado"
              name="installmentNegotiatedValue"
              required={true}
            />
            <InputMoney
              label="valor da entrada"
              name="downPayment"
              required={true}
            />
            <InputFormik
              label="Data de cobrança da primeira parcela"
              name={'nextDueDate'}
              type="date"
              required={true}
            />
            <ComboBoxFormik
              name="installmentOption"
              label="Escolha a opção de parcelamento:"
              options={installmentOptions}
              required={true}
            />
            <ComboBoxFormik
              name="paymentOptions"
              label="Escolha a opção de pagamento:"
              options={paymentOptions}
              required={true}
            />


          </section>
          <TextAreaFormik
            name="description"
            label="Descrição"
            placeholder="Digite a descrição da negociação"
          />
        </div>

        <div className="w-full flex mt-10 justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}>
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  )
}